import { z } from 'zod';

export const CAMPAIGN_TABS = [
  'details',
  'reporting',
  'creators',
  'confirmed',
  'scripts',
  'content',
  'live-content',
  'settings',
  'shopify',
  'discovery',
  'outreach',
] as const;

export type CampaignTab = (typeof CAMPAIGN_TABS)[number];

export const DISCOVERY_TAB: CampaignTab = 'discovery';

export const DISCOVERY_SUB_VIEWS = ['find', 'personas', 'pool'] as const;
export type DiscoverySubView = (typeof DISCOVERY_SUB_VIEWS)[number];

export const isDiscoverySubView = (value: string | undefined): value is DiscoverySubView =>
  DISCOVERY_SUB_VIEWS.some((view) => view === value);

export const FULL_BLEED_TABS: ReadonlySet<CampaignTab> = new Set<CampaignTab>([DISCOVERY_TAB]);

/** Old `?tab=access-control` deep links: kept in the schema so the page can redirect them to settings. */
export const LEGACY_ACCESS_TAB = 'access-control' as const;

export const DEFAULT_CAMPAIGN_TAB: CampaignTab = 'creators';
export const LIVE_CAMPAIGN_LANDING_TAB: CampaignTab = 'reporting';
export const CONFIRMED_LANDING_TAB: CampaignTab = 'confirmed';

export const CAMPAIGN_SETTINGS_SUBS = [
  'access',
  'integrations',
  'outreach',
  'pipeline',
  'controls',
  'links',
] as const;
export type CampaignSettingsSub = (typeof CAMPAIGN_SETTINGS_SUBS)[number];

/** Outreach settings used to be an outreach sub-view (`?tab=outreach&sub=settings`);
 *  old links still carry that pair. */
const LEGACY_OUTREACH_SETTINGS_SUB = 'settings';

/** The settings sub-tab a legacy campaign deep link now lives under, or null
 *  for a current link. Takes raw (pre-validation) values so search hygiene can
 *  ask too. */
export function legacySettingsSub(search: { tab?: unknown; sub?: unknown }): CampaignSettingsSub | null {
  if (search.tab === LEGACY_ACCESS_TAB) return 'access';
  if (search.tab === 'outreach' && search.sub === LEGACY_OUTREACH_SETTINGS_SUB) return 'outreach';
  return null;
}

const pageNumber = z.coerce.number().int().positive().optional().catch(undefined);

export const OUTREACH_CRM_TABS = ['overview', 'creators'] as const;
export type OutreachCrmTab = typeof OUTREACH_CRM_TABS[number];

export const OUTREACH_CRM_VIEWS = ['all', 'conversations', 'rates'] as const;
export type OutreachCrmView = typeof OUTREACH_CRM_VIEWS[number];

// zod v4 = Standard Schema → pass directly to validateSearch. Per-field .catch() = resilient fallback.
export const campaignSearchSchema = z.object({
  // Absent or garbage `?tab` stays undefined: the page picks the landing tab
  // from campaign state, which a default here would hide.
  tab: z
    .enum([...CAMPAIGN_TABS, LEGACY_ACCESS_TAB])
    .optional()
    .catch(undefined),
  sub: z.string().optional().catch(undefined),
  panel: z
    .enum([
      'script-review',
      'content-review',
      'script-finalized',
      'content-finalized',
      'brand-safety',
      'insights',
      'conversation',
      'video',
    ])
    .optional()
    .catch(undefined),
  // `video` = roster_entry_id of the entry whose live video popup is open.
  // Stable across the three video surfaces (ConfirmedTab / LiveContentTab /
  // reporting CampaignCreatorContentTab); the active `?tab=` disambiguates
  // which surface displays the popup.
  video: z.string().optional().catch(undefined),
  // `focus` = entry a shared link points at; like `video` it only makes sense on the roster tabs.
  focus: z.string().optional().catch(undefined),
  // `creator` = platform username (unique per platform; a campaign is one
  // platform). Readable + shareable (e.g. ?creator=mrbeast) vs an opaque id.
  // Trade-off (accepted): a username change breaks old links.
  creator: z.string().optional().catch(undefined),
  // `thread=open` deep-links a review panel (script/content) with its thread
  // column already expanded. Enum (single value) keeps it forward-compatible
  // and resilient: an unknown value falls back to undefined (thread closed).
  thread: z.enum(['open']).optional().catch(undefined),
  // List page (1-based), URL-owned. Coerced from the string; invalid/absent →
  // undefined (treated as page 1 by the consumer). Omitted from URLs when 1.
  page: z.coerce.number().int().positive().optional().catch(undefined),
  // Rows per page for the active roster table, URL-owned so a shared link
  // reproduces the same view. Constrained to the offered presets — anything
  // else (or absent) falls back to undefined = the default page size.
  size: z.coerce.number().int().positive().optional().catch(undefined),
  // List sub-filter ("pill") for the active tab; free string, each tab maps it.
  pill: z.string().optional().catch(undefined),
  // Creator Content's search term, URL-owned so a refresh or shared link keeps it.
  search: z.string().optional().catch(undefined),
  // Active column sort for the active tab/pill, URL-owned so a shared link
  // reproduces the exact sorted view — mirrors page/pill. `sort_by` is a free
  // string (each tab/pill validates it against its own allowlist server-side;
  // an unrecognized value just no-ops back to the default order, same as
  // typing a stale one). Absent = unsorted (default recency). Per-pill sort
  // MEMORY still lives in sessionStorage (useRosterTabSort) — this field is a
  // read/write mirror of whichever pill is currently active, not a second
  // independent store.
  sort_by: z.string().optional().catch(undefined),
  sort_dir: z.enum(['asc', 'desc']).optional().catch(undefined),
  // Active sub-tab inside a review panel (script/content), e.g. "overview",
  // "issues", "geo". Lets the share button deep-link to the exact view.
  stab: z.string().optional().catch(undefined),
  // Outreach template editor target ("new" | "fork:<id>" | "<id>"). A numeric id
  // stays a number: the router quotes a numeric-looking string in the URL
  // (`template=%226%22`, E2E R6-n). Consumers read it through String().
  template: z
    .union([z.string(), z.number()])
    .transform((v) => (typeof v === 'string' && /^\d+$/.test(v) ? Number(v) : v))
    .optional()
    .catch(undefined),
  // Roster entry whose outreach thread is open.
  entry: z.string().optional().catch(undefined),
  // Reporting window preset (Task 12) for the two windowed reporting
  // sub-tabs — General Insights (sub=overview) and Sentiment Intelligence
  // (sub=sentiment). ABSENT means "no explicit selection was ever written to
  // the URL" (a pre-Task-12 link, or the very first load before any dropdown
  // interaction) — the consumer falls back to reportingWindow.ts's
  // DEFAULT_TIME_PERIOD. An explicit `full` is a DELIBERATE user choice of
  // Full Campaign and must NEVER be treated the same as absent — mirrors the
  // BE's identical empty-vs-explicit-`full` distinction on the share-grant
  // `window` field (see the sibling BE Task 11 fix for the same bug class).
  win: z.enum(['1w', '1m', '6m', 'full']).optional().catch(undefined),
  persona: z.string().optional().catch(undefined),
  chat: z.string().optional().catch(undefined),
  run: z.string().optional().catch(undefined),
  result: z.enum(['passed', 'review', 'rejected']).optional().catch(undefined),
  // Outreach Inbox FilterPanel (Task 25) — reached-on date range, stage
  // (comma-joined outreach status values), reply intent, and a locked-rate
  // flag. All optional/undefined-catch like every other filter field above.
  reached_from: z.string().optional().catch(undefined),
  reached_to: z.string().optional().catch(undefined),
  stage: z.string().optional().catch(undefined),
  intent: z.string().optional().catch(undefined),
  locked_rate: z.coerce.boolean().optional().catch(undefined),
  // Outreach Inbox's own search box and column sort. Deliberately NOT the
  // shared `sort_by`/`sort_dir` fields: those mirror whichever ROSTER TAB's
  // pill is active (useRosterTabSort's sessionStorage store) and switching
  // tabs resets `pill`/`page` but never touches them — reusing them here
  // would both leak a stale roster-tab sort value into the Inbox on tab
  // entry and let the Inbox's own sort value get picked up by that mirror
  // logic and overwrite another tab's persisted sort.
  outreach_q: z.string().optional().catch(undefined),
  outreach_sort: z.enum(['last_activity', 'name', 'status', 'scheduled_at']).optional().catch(undefined),
  // Per-table page/size for views that show several paged tables at once, so
  // each table owns its own URL state (see pageParamKeys.ts). Same coercion as
  // `page` / `size`.
  sc_active_page: pageNumber,
  sc_active_size: pageNumber,
  sc_leftover_page: pageNumber,
  sc_leftover_size: pageNumber,
  sc_replaced_page: pageNumber,
  sc_replaced_size: pageNumber,
  sc_inactive_page: pageNumber,
  sc_inactive_size: pageNumber,
  sc_renamed_page: pageNumber,
  sc_renamed_size: pageNumber,
  // The coupon list's applied search term and live filter; setting either also
  // clears the list pages in the same navigation.
  sc_q: z.string().optional().catch(undefined),
  sc_live: z.enum(['live', 'not_live']).optional().catch(undefined),
  cv_creator_page: pageNumber,
  cv_creator_size: pageNumber,
  cv_campaign_page: pageNumber,
  cv_campaign_size: pageNumber,
  // Outreach CRM's own keys: its Overview/Creators tab, the Creators tab's
  // list view, and that list's search box. Kept apart from the Inbox's
  // `outreach_q` so a search typed in one view never filters the other.
  crm_tab: z.enum(OUTREACH_CRM_TABS).optional().catch(undefined),
  crm_view: z.enum(OUTREACH_CRM_VIEWS).optional().catch(undefined),
  crm_q: z.string().optional().catch(undefined),
  cv_refused_page: pageNumber,
  cv_refused_size: pageNumber,
});

/** Per-table list state of the Shopify coupon lists and conversions tables
 *  (pages, sizes, search term, live filter). Cleared when the tab is left, so a
 *  stale `?sc_active_page=4` cannot follow the user into another campaign view. */
export const SHOPIFY_LIST_PARAM_KEYS = [
  'sc_active_page', 'sc_active_size', 'sc_leftover_page', 'sc_leftover_size',
  'sc_replaced_page', 'sc_replaced_size', 'sc_inactive_page', 'sc_inactive_size',
  'sc_renamed_page', 'sc_renamed_size', 'sc_q', 'sc_live',
  'cv_creator_page', 'cv_creator_size', 'cv_campaign_page', 'cv_campaign_size',
  'cv_refused_page', 'cv_refused_size',
] as const;

export const clearedShopifyListParams = (): Record<(typeof SHOPIFY_LIST_PARAM_KEYS)[number], undefined> =>
  Object.fromEntries(SHOPIFY_LIST_PARAM_KEYS.map((k) => [k, undefined])) as never;

export type CampaignSearch = z.infer<typeof campaignSearchSchema>;

/** Cleared when leaving Discovery: a stale `?creator=` would otherwise open the roster panel on Creators. */
export const DISCOVERY_PARAMS: readonly (keyof CampaignSearch)[] = [
  'sub',
  'run',
  'result',
  'persona',
  'chat',
  'creator',
];

export const LIST_PARAMS: readonly (keyof CampaignSearch)[] = ['pill', 'page'];

export const withoutParams = (params: readonly (keyof CampaignSearch)[]): Partial<CampaignSearch> =>
  Object.fromEntries(params.map((param) => [param, undefined]));

export const withoutDiscoveryParams = (): Partial<CampaignSearch> =>
  withoutParams(DISCOVERY_PARAMS);

export const readCampaignSearch = (raw: unknown): CampaignSearch => campaignSearchSchema.parse(raw);

// ── Campaigns LIST routes ────────────────────────────────────────────────────
// Used by the brand and admin "All campaigns" tables. Splitting from
// campaignSearchSchema because the list route has different params (no tab,
// no panel) and the validator must reject keys it doesn't know.
export const campaignsListSearchSchema = z.object({
  // 1-based page. Omitted from the URL when on page 1 (URL stays clean for
  // the common case). Coerced from the string form; invalid input → undefined
  // (consumer treats undefined as page 1).
  page: z.coerce.number().int().positive().optional().catch(undefined),
  // Set by the public short-link resolver's not-found redirect (?link=not-found); see spec §8
  link: z.string().optional().catch(undefined),
});

export type CampaignsListSearch = z.infer<typeof campaignsListSearchSchema>;
